import { useFilterOptions, FilterOptionsStatus } from '../filter-options';
import { countryName } from '@/services/photovogue/country-name';

import { Button } from '@repo/ui/components/ui/button';
import { Checkbox } from '@repo/ui/components/ui/checkbox';
import { Label } from '@repo/ui/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/ui/sheet';
import {
  PHOTOGRAPHER_STATE_LABELS,
  PHOTOGRAPHER_STATES,
  type PhotographerFilters,
  type PhotographerState,
} from '@/services/photovogue';

export interface PhotographerFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PhotographerFilters;
  updateFilters: (
    updater:
      | PhotographerFilters
      | ((filters: PhotographerFilters) => PhotographerFilters),
  ) => void | Promise<void>;
  resetFilters: () => void | Promise<void>;
}

const toggleState = (
  states: PhotographerState[] | undefined,
  value: PhotographerState,
): PhotographerState[] | undefined => {
  const next = new Set(states ?? []);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next.size ? [...next] : undefined;
};

export const PhotographerFilterPanel = ({
  open,
  onOpenChange,
  filters,
  updateFilters,
  resetFilters,
}: PhotographerFilterPanelProps) => {
  const countries = useFilterOptions(open, 'countries');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Photographer filters</SheetTitle>
          <SheetDescription>
            Refine photographers by moderation state, tier, country, and photo
            count.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cn-muted-foreground">
              State
            </p>
            <div className="space-y-2">
              {PHOTOGRAPHER_STATES.map((state) => (
                <Label
                  key={state}
                  className="flex cursor-pointer items-center gap-2 text-sm font-normal"
                >
                  <Checkbox
                    checked={(filters.states ?? []).includes(state)}
                    onCheckedChange={() =>
                      updateFilters((current) => ({
                        ...current,
                        states: toggleState(current.states, state),
                      }))
                    }
                  />
                  {PHOTOGRAPHER_STATE_LABELS[state]}
                </Label>
              ))}
            </div>
          </section>

          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cn-muted-foreground">
              Tier
            </p>
            <div className="flex gap-2">
              {(['basic', 'gold'] as const).map((tier) => (
                <Button
                  key={tier}
                  type="button"
                  size="sm"
                  variant={filters.tier === tier ? 'default' : 'outline'}
                  onClick={() =>
                    updateFilters((current) => ({
                      ...current,
                      tier: current.tier === tier ? undefined : tier,
                    }))
                  }
                >
                  {tier === 'gold' ? 'Spotlight' : 'Basic'}
                </Button>
              ))}
            </div>
          </section>

          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cn-muted-foreground">
              Country
            </p>
            <FilterOptionsStatus label="countries" {...countries} />
            <select
              aria-label="Country"
              aria-busy={countries.loading}
              disabled={countries.loading || countries.error}
              value={filters.country ?? ''}
              onChange={(event) =>
                updateFilters((current) => ({
                  ...current,
                  country: event.target.value || undefined,
                }))
              }
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">All countries</option>
              {[...countries.values]
                .sort((a, b) => countryName(a).localeCompare(countryName(b)))
                .map((country) => (
                  <option key={country} value={country}>
                    {countryName(country)}
                  </option>
                ))}
            </select>
          </section>

          <Label className="flex cursor-pointer items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={!filters.withPhotosOnly}
              onCheckedChange={(checked) =>
                updateFilters((current) => ({
                  ...current,
                  withPhotosOnly: checked === true ? undefined : true,
                }))
              }
            />
            Show photographers with 0 photos
          </Label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => resetFilters()}>
              Reset
            </Button>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PhotographerFilterPanel;
